import { UnifiedSelect } from "@cmp-src/form/UnifiedSelect";
import type { SelectItem } from "@cmp-src/form/UnifiedSelect/types";
import type { ScheduleBuffer } from "@gettactic/api";
import { cn } from "@gettactic/helpers";
import {
  BUFFER_MINUTE_OPTIONS,
  formatBufferMinutes
} from "@gettactic/helpers/src/schedules/scheduleBuffer";
import { format } from "date-fns";
import { Lock } from "lucide-react";
import React from "react";

export const EMPTY_BUFFER: ScheduleBuffer = {
  before_minutes: 0,
  after_minutes: 0
};

interface BufferSideSelectProps {
  label: string;
  value: number;
  onChange: (minutes: number) => void;
  disabled?: boolean;
}

/**
 * `UnifiedSelect`, not the Radix `Select`: the map's reservation card stops
 * repainting in Chrome when a Radix Select closes inside it, and every other
 * picker on that card already uses this one.
 */
const BufferSideSelect: React.FC<BufferSideSelectProps> = ({
  label,
  value,
  onChange,
  disabled
}) => {
  // Core accepts any whole minute, so a stored length outside the preset list
  // is offered as its own option rather than shown as an empty picker.
  const minutesOptions = BUFFER_MINUTE_OPTIONS.includes(value)
    ? BUFFER_MINUTE_OPTIONS
    : [...BUFFER_MINUTE_OPTIONS, value].sort((a, b) => a - b);
  const items: SelectItem[] = minutesOptions.map((minutes) => ({
    id: String(minutes),
    name: formatBufferMinutes(minutes)
  }));

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <span className="font-medium text-neutral-600 text-xs uppercase tracking-wide">
        {label}
      </span>
      <UnifiedSelect
        items={items}
        value={items.find((item) => item.id === String(value)) ?? null}
        onSelect={(item) => onChange(Number(item?.id ?? 0))}
        disabled={disabled}
        triggerClassName="w-full"
      />
    </div>
  );
};

export interface ReservationBufferFieldsProps {
  value: ScheduleBuffer;
  onChange: (value: ScheduleBuffer) => void;
  disabled?: boolean;
  className?: string;
}

/**
 * The pair of pickers that set the preparation and cleanup time a reservation
 * holds the resource for. Both sides at "None" means the office's own buffer
 * applies — these only ever add time on top of it.
 */
export const ReservationBufferFields: React.FC<
  ReservationBufferFieldsProps
> = ({ value, onChange, disabled, className }) => (
  <div className={cn("grid grid-cols-2 gap-3", className)}>
    <BufferSideSelect
      label="Before"
      value={value.before_minutes}
      disabled={disabled}
      onChange={(minutes) => onChange({ ...value, before_minutes: minutes })}
    />
    <BufferSideSelect
      label="After"
      value={value.after_minutes}
      disabled={disabled}
      onChange={(minutes) => onChange({ ...value, after_minutes: minutes })}
    />
  </div>
);

export interface ReservationBufferSummaryProps {
  /** The reservation's own period. */
  start: Date;
  end: Date;
  buffer: ScheduleBuffer;
  className?: string;
}

const formatClock = (date: Date) => format(date, "h:mmaaa");

/**
 * The window the resource ends up blocked for, spelled out under the pickers.
 * The reminder that the meeting itself did not move is the whole point: a
 * buffer holds the room around the booking, it never changes it.
 */
export const ReservationBufferSummary: React.FC<
  ReservationBufferSummaryProps
> = ({ start, end, buffer, className }) => {
  const before = Math.max(0, buffer.before_minutes);
  const after = Math.max(0, buffer.after_minutes);
  if (!before && !after) return null;

  const blockedStart = new Date(start.getTime() - before * 60_000);
  const blockedEnd = new Date(end.getTime() + after * 60_000);

  return (
    <div className={cn("flex flex-col gap-0.5 text-xs", className)}>
      <p className="flex items-center gap-1.5 text-neutral-800">
        <Lock aria-hidden="true" className="h-3.5 w-3.5 text-primary" />
        <span>
          Room blocked{" "}
          <span className="font-semibold">
            {formatClock(blockedStart)} — {formatClock(blockedEnd)}
          </span>
        </span>
      </p>
      <p className="text-neutral-600">· meeting time unchanged</p>
    </div>
  );
};
